/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import { IconWrapper } from "../../customHooks/useIcon";
import { FaChevronDown, FaChevronUp, FaSearch, } from "react-icons/fa";
import { IHierarchicalNode } from "../../utils/interfaces";

interface HierarchicalDeviceSelectorProps {
  hierarchy: IHierarchicalNode[];
  selectedDevices: string[];
  onChange: (devices: string[]) => void;
  selectionField?: "gateway_serial" | "asset_name";
  placeholder?: string;
  className?: string;
}

const HierarchicalDeviceSelector: React.FC<HierarchicalDeviceSelectorProps> = ({
  hierarchy,
  selectedDevices,
  onChange,
  selectionField = "gateway_serial",
  placeholder = "Search and select devices...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get the selection value from a device based on the selectionField
  const getDeviceSelectionValue = (device: any): string => {
    return selectionField === "asset_name"
      ? device.asset_name
      : device.gateway_serial;
  };

  // Get device display name
  const getDeviceDisplayName = (device: any): string => {
    return device.asset_name || device.gateway_serial;
  };

  // Get all selection values from a node and its children
  const getAllSelectionValues = (node: IHierarchicalNode): string[] => {
    const deviceSelectionValues =
      node?.devices &&
      node.devices.map((device) => getDeviceSelectionValue(device));
    const childrenSelectionValues = node.children.flatMap((child) =>
      getAllSelectionValues(child)
    );
    return [...(deviceSelectionValues || []), ...childrenSelectionValues];
  };

  // Get all devices from hierarchy for search
  const getAllDevices = (nodes: IHierarchicalNode[]): any[] => {
    const devices: any[] = [];

    const collectDevices = (node: IHierarchicalNode) => {
      if (node.devices) {
        devices.push(...node.devices);
      }
      node.children.forEach(collectDevices);
    };

    nodes.forEach(collectDevices);
    return devices;
  };

  // Filter devices based on search term
  const filteredDevices = getAllDevices(hierarchy).filter((device) =>
    getDeviceDisplayName(device)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Check if all devices in a node and its children are selected
  const isNodeFullySelected = (node: IHierarchicalNode): boolean => {
    if (
      node?.devices &&
      node.devices.length === 0 &&
      node.children.length === 0
    ) {
      return false;
    }

    const devicesSelected =
      (node?.devices && node.devices.length === 0) ||
      (node?.devices &&
        node.devices.every((device) =>
          selectedDevices.includes(getDeviceSelectionValue(device))
        ));

    const childrenSelected =
      node.children.length === 0 ||
      node.children.every((child) => isNodeFullySelected(child));

    return !!devicesSelected && !!childrenSelected;
  };

  // Check if some but not all devices in a node and its children are selected
  const isNodePartiallySelected = (node: IHierarchicalNode): boolean => {
    if (getAllSelectionValues(node).length === 0) {
      return false;
    }

    const anyDeviceSelected =
      node?.devices &&
      node.devices.some((device) =>
        selectedDevices.includes(getDeviceSelectionValue(device))
      );

    const anyChildSelected = node.children.some(
      (child) => isNodePartiallySelected(child) || isNodeFullySelected(child)
    );

    return (
      (anyDeviceSelected || anyChildSelected) && !isNodeFullySelected(node)
    );
  };

  // Toggle selection of a node
  const toggleNodeSelection = (
    node: IHierarchicalNode,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }

    const allSelectionValues = getAllSelectionValues(node);

    if (allSelectionValues.length === 0) {
      return;
    }

    if (isNodeFullySelected(node)) {
      const newSelection = selectedDevices.filter(
        (value) => !allSelectionValues.includes(value)
      );
      onChange(newSelection);
    } else {
      const newSelection = [
        ...selectedDevices.filter(
          (value) => !allSelectionValues.includes(value)
        ),
        ...allSelectionValues,
      ];
      onChange(newSelection);
    }
  };

  // Toggle a single device
  const toggleDeviceSelection = (
    selectionValue: string,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }

    if (selectedDevices.includes(selectionValue)) {
      onChange(selectedDevices.filter((value) => value !== selectionValue));
    } else {
      onChange([...selectedDevices, selectionValue]);
    }
  };

  // Toggle expand/collapse of a node
  const toggleExpand = (nodeId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    setExpandedNodes((prev) =>
      prev.includes(nodeId)
        ? prev.filter((id) => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  // Filter hierarchy based on search term
  const filterHierarchy = (
    nodes: IHierarchicalNode[],
    searchTerm: string
  ): IHierarchicalNode[] => {
    if (!searchTerm) return nodes;

    return nodes.reduce((filtered: IHierarchicalNode[], node) => {
      const nodeMatches = node.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const filteredDevices =
        node.devices?.filter((device) =>
          getDeviceDisplayName(device)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        ) || [];
      const filteredChildren = filterHierarchy(node.children, searchTerm);

      if (
        nodeMatches ||
        filteredDevices.length > 0 ||
        filteredChildren.length > 0
      ) {
        filtered.push({
          ...node,
          devices: filteredDevices,
          children: filteredChildren,
        });
      }

      return filtered;
    }, []);
  };

  const filteredHierarchy = filterHierarchy(hierarchy, searchTerm);

  // Recursively render a node and its children
  const renderNode = (node: IHierarchicalNode, level = 0) => {
    const isExpanded = expandedNodes.includes(node.id);
    const hasChildren =
      node.children.length > 0 || (node?.devices && node.devices.length > 0);
    const isFullySelected = isNodeFullySelected(node);
    const isPartiallySelected = isNodePartiallySelected(node);
    const hasSelectableItems = getAllSelectionValues(node).length > 0;

    const paddingLeft = level * 4;

    return (
      <div key={node.id} className="w-full">
        <div
          className={`flex items-center p-2 hover:bg-gray-50`}
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
        >
          <div className="flex items-center cursor-pointer w-full">
            <div
              className="flex cursor-pointer w-4/5 max-w-[80%] items-center"
              onClick={(e) => {
                e.stopPropagation();
                toggleNodeSelection(node);
              }}
            >
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-indigo-600"
                checked={isFullySelected}
                disabled={!hasSelectableItems}
                ref={(input) => {
                  if (input) {
                    input.indeterminate =
                      isPartiallySelected && !isFullySelected;
                  }
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleNodeSelection(node);
                }}
              />
              <span
                className={`ml-2 font-medium text-sm ${
                  !hasSelectableItems ? "text-gray-400" : ""
                }`}
              >
                {node.name}
              </span>
              <span className="ml-1 text-gray-500 text-xs">
                {node.asset_count
                  ? node.asset_count > 0 && `(${node.asset_count})`
                  : ""}
              </span>
            </div>
            {hasChildren && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}
                className="text-gray-600 focus:outline-none w-1/5 max-w-[20%] flex items-center justify-center"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <IconWrapper icon={FaChevronDown} className="h-3 w-3" />
                ) : (
                  <IconWrapper icon={FaChevronUp} className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div>
            {node.children.map((child) => renderNode(child, level + 1))}

            {node.devices &&
              node.devices.map((device) => {
                const deviceSelectionValue = getDeviceSelectionValue(device);
                return (
                  <div
                    key={device.id}
                    className="flex items-center p-2 hover:bg-gray-50"
                    style={{ paddingLeft: `${(level + 1) * 4 + 24}px` }}
                  >
                    <div
                      className="flex items-center cursor-pointer w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDeviceSelection(deviceSelectionValue);
                      }}
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-indigo-600"
                        checked={selectedDevices.includes(deviceSelectionValue)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleDeviceSelection(deviceSelectionValue);
                        }}
                      />
                      <span className="ml-2 text-sm">
                        {getDeviceDisplayName(device)}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  // Handle "Select All" option
  const handleSelectAll = (selected: boolean, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    if (selected) {
      const allSelectionValues = hierarchy.flatMap(getAllSelectionValues);
      onChange(allSelectionValues);
    } else {
      onChange([]);
    }
  };

  // Check if all devices are selected
  const allSelectionValues = hierarchy.flatMap(getAllSelectionValues);
  const allSelected =
    allSelectionValues.length > 0 &&
    allSelectionValues.every((value) => selectedDevices.includes(value));

  // Check if some devices are selected
  const someSelected = selectedDevices.length > 0 && !allSelected;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="min-w-[200px] h-[2rem] flex items-center justify-between py-1 px-3 rounded-full bg-[#E5E5E5] text-primary-blackMain text-[10px] cursor-pointer hover:bg-gray-200 transition-colors"
      >
        <span className="truncate">
          {selectedDevices.length > 0
            ? `${selectedDevices.length} device${
                selectedDevices.length !== 1 ? "s" : ""
              } selected`
            : placeholder}
        </span>
        <IconWrapper
          icon={isOpen ? FaChevronUp : FaChevronDown}
          className="h-3 w-3 ml-2 flex-shrink-0"
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-96 flex flex-col">
          {/* Search input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <IconWrapper
                icon={FaSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Dropdown content */}
          <div className="overflow-y-auto flex-1">
            {/* "All Devices" option */}
            <div className="border-b border-gray-100">
              <div
                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(!allSelected);
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll(e.target.checked);
                  }}
                  className="form-checkbox h-4 w-4 text-indigo-600"
                />
                <span className="ml-2 text-sm font-medium">All Devices</span>
              </div>
            </div>

            {/* Render hierarchy or search results */}
            <div className="py-1">
              {searchTerm ? (
                // Show filtered devices when searching
                filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => {
                    const deviceSelectionValue =
                      getDeviceSelectionValue(device);
                    return (
                      <div
                        key={device.id}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDeviceSelection(deviceSelectionValue);
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600"
                          checked={selectedDevices.includes(
                            deviceSelectionValue
                          )}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleDeviceSelection(deviceSelectionValue);
                          }}
                        />
                        <span className="ml-2 text-sm">
                          {getDeviceDisplayName(device)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-3 text-gray-500 text-sm">
                    No devices found
                  </div>
                )
              ) : (
                // Show hierarchy when not searching
                filteredHierarchy.map((node) => renderNode(node))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchicalDeviceSelector;
